import { Component, OnInit } from '@angular/core';
import { PagedResponse } from '@shared/models/paged-response';
import { PageEvent } from '@shared/models/page-event';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ApiService } from '@shared/apis';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import Utils from '@shared/utils/utils';
import { Dealer } from '@shared/models/dealer';
import { FormControl } from '@angular/forms';
import { SpinnerService } from '@shared/services/spinner.service';
import { DealerEditComponent } from '@routes/dealers/dealer-edit/dealer-edit.component';
import { AuthService } from '@core/auth/auth.service';
import Roles from '@shared/constants/roles';
import { Constants } from '@shared/constants/constants';
import Role from '@shared/constants/role';
import { Company } from '@shared/enums/company';
import { Options } from '@shared/models/options';
import { ParamsBuilder } from '@shared/models/params-builder';
import DateUtils from '@shared/utils/date-utils';
import Configs from '@shared/constants/configs';
import { FileService } from '@shared/services/file.service';
import { environment } from '@env/environment';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-dealer',
  templateUrl: './dealer.component.html',
  styleUrls: ['./dealer.component.scss']
})
export class DealerComponent implements OnInit {
  readonly configs = Configs;
  readonly backend = environment.api.backend;
  realPage: number;
  showDetail: boolean;
  pagedResponse: PagedResponse;

  query: any;
  q: any;
  searchText = new FormControl(null);
  dealerData!: Dealer[];
  selectedData: Dealer;
  dealerSelectGroup!: Dealer[];

  newDealers: any;
  bsModalRef: BsModalRef;

  showNew = false;
  isAdmin = false;
  isAdminOnly = false;
  isCreatable = false;
  isEditable = false;
  isDealerSupervisor = false;
  company;
  companies = Company;
  isHq = false;
  isProductView = false;
  isAdminViewerRole: boolean;

  constructor(
    private modalService: BsModalService,
    private spinnerService: SpinnerService,
    private authService: AuthService,
    private apiService: ApiService,
    private fileService: FileService,
  ) {
    this.showNew = this.authService.hasType(Roles.dealerNewView);
    this.isAdmin = this.authService.hasType(Roles.dealerNewView);
    this.isAdminOnly = this.authService.hasType(Roles.adminOnly);
    this.isHq = this.authService.isHq();
    this.company = this.authService.getCurrentCompany();
    this.isDealerSupervisor = this.authService.hasRoleInCompany(Role.secaDealerSuperVisor);
    this.isAdminViewerRole = this.authService.checkHasAdminViewerRole();
  }


  ngOnInit(): void {
    this.isCreatable = this.isAdmin || Company.Sea === this.company;
    this.getDealerList();
    this.showList();
    this.showNewList();
    // this.getNewDealers();
    this.searchText.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(text => {
        this.q = text;
        this.showList();
      });
    if (!this.showNew) {
      const { userType, hq } = this.authService.getToken();
      if (([this.companies.Sea].includes(this.company) && Constants.userType.dealer === userType && hq) ||
        ([this.companies.Seca].includes(this.company) && Constants.userType.dealer === userType && hq && this.isDealerSupervisor)) {
        this.showNew = true;
      }
    }
    if ([this.companies.Sea].includes(this.company) && this.isAdmin) {
      this.showNew = !this.isAdminViewerRole;
    }
  }

  onPageEvent(pageEvent: PageEvent) {
    this.showList(pageEvent);
  }

  getDealerList() {
    this.dealerSelectGroup = this.authService.getHQDealers();
  }

  changeDealer(e) {
    if (e && e.bpNo) {
      this.query = { $or: [{ bpNo: e.bpNo }, { hqBpNo: e.bpNo }] };
    } else {
      this.query = {};
    }
    this.showList();
  }


  showList(pageEvent?: PageEvent) {
    this.spinnerService.start();
    this.apiService.dealer.getDealers(pageEvent, this.q, this.query)
      .pipe(finalize(() => this.spinnerService.stop()))
      .subscribe(res => {
        this.pagedResponse = res;
        this.dealerData = res.docs.map(m => {
          const contactPersonCount = m.contacts ? m.contacts.length : 0;
          if (m.contacts && m.contacts.length > 0) {
            const remainingCount = m.contacts.length - 1;
            const contactNames = m.contacts.slice(0, 1).map(c => `${ c.firstName } ${ c.lastName }`);
            m.contactPerson = `${ contactNames }${ remainingCount > 0 ? `, + ${ remainingCount }` : '' }`;
          } else {
            m.contactPerson = '';
          }
          return m;
        });
      }, err => Utils.showError(err));
  }

  showNewList() {
    const pageEvent = new PageEvent(1, 100);
    this.spinnerService.start();
    this.apiService.dealer.getNewDealers(pageEvent, this.q, this.query)
      .pipe(finalize(() => this.spinnerService.stop()))
      .subscribe(res => {
        this.newDealers = res.docs;
      }, err => Utils.showError(err));
  }

  modalOpen() {
    const initialState = {
      id: 1, title: 'Dealer Create', buttonLabel: 'Create'
    };

    this.bsModalRef = this.modalService.show(DealerEditComponent, { initialState, class: 'modal-lg' });
    this.bsModalRef.content.onClose.subscribe(result => {
      if (result) {
        this.getDealerList();
        this.showNewList();
        this.showList();
      }
    });
  }

  onBoarding(obj) {
    const initialState = {
      id: 1,
      title: 'Dealer Information',
      onboarding: obj.onboarding,
      dealerInfo: obj,
    };
    this.bsModalRef = this.modalService.show(DealerEditComponent, { initialState, class: 'modal-lg' });
    this.bsModalRef.content.onClose.subscribe(result => {
      this.reload(result);
    });
  }

  selectRow(dealer) {
    if (dealer === this.selectedData) {
      return;
    }
    this.showDetailInfo(dealer);
  }

  showDetailInfo(dealer) {
    const { bpNo } = dealer;

    this.spinnerService.start();
    this.apiService.dealer.getDealer(bpNo)
      .pipe(finalize(() => this.spinnerService.stop()))
      .subscribe(res => {
        this.selectedData = res;
        this.showDetail = true;
      }, err => Utils.showError(err));
  }

  reload(dealer) {
    this.showList();
    this.showDetail = false;
    if (dealer) {
      this.showDetailInfo(dealer);
    }
    this.showNewList();
  };

  options() {
    const opt: Options = {
      q: this.q,
      query: this.query,
    };
    return opt;
  }

  exportResult(pageEvent?: PageEvent) {
    const opt: Options = this.options();

    if (!pageEvent) {
      pageEvent = new PageEvent();
      pageEvent.limit = this.configs.pagination.sizeOptions[0];
      this.realPage = pageEvent.page;
    }

    const builder = new ParamsBuilder();
    builder.pageEvent = pageEvent;

    if (opt?.q) {
      builder.q = opt.q;
    }
    if (opt?.query) {
      builder.query = opt.query;
    }
    if (opt?.filter) {
      builder.filter = opt.filter;
    }
    if (this.pagedResponse.totalDocs >= 1000) {
      builder.export = 'email';
      this.spinnerService.start('table');
      this.apiService.dealer.exportDealers(builder.build())
        .pipe(finalize(() => this.spinnerService.stop('table')))
        .subscribe(res => {
          Swal.fire({
            title: 'Dealer List was exported',
            text: 'Dealer list would be sent to your email address.',
            icon: 'success',
            allowOutsideClick: false,
          }).then((result) => {
          });
        }, err => Utils.showError(err));
    } else {
      builder.export = 'download';
      const isPost = true;
      const fileName = `DEALER-${ DateUtils.toHqMomentFormat(new Date(), 'YYYYMMDDHHmmssSSS') }`;
      this.fileService.downloadFile(this.backend.concat('/dealer/export'), fileName, builder.build(), isPost);
    }
  }


}
